import React, { useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { UserSessionContext } from 'src/context/UserSessionContext';

import Login from 'src/pages/Login/Login';
import Home from 'src/pages/Home/Home';
import PageNotFound from 'src/pages/PageNotFound/PageNotFound';
import Design from 'src/pages/Design/Design';
import GameDashboardGlass from 'src/pages/Design/pages/GameDashboardGlass/GameDashboardGlass';
import CatParachuteParallax from 'src/pages/Design/pages/CatParachuteParallax/CatParachuteParallax';
import InitialBeginnerParallax from 'src/pages/Design/pages/InitialBeginnerParallax/InitialBeginnerParallax';
import SimpleBasicGrid from 'src/pages/Design/pages/FireshipSimpleGrids/SimpleBasicGrid';
import SimplePhotoGrid from 'src/pages/Design/pages/FireshipSimpleGrids/SimplePhotoGrid';
import SimpleAnimatedGrid from 'src/pages/Design/pages/FireshipSimpleGrids/SimpleAnimatedGrid';
import ForestWelcomeParallax from 'src/pages/Design/pages/ForestWelcomeParallax/ForestWelcomeParallax';
import StayAfloatAnimation from 'src/pages/Design/pages/StayAfloatAnimation/StayAfloatAnimation';
import BeginnerGsapAnimation from 'src/pages/Design/pages/BeginnerGsapAnimation/BeginnerGsapAnimation';

import * as globalConstants from 'src/constants/index';
import ContentGuard from 'src/layout/ContentGuard/ContentGuard';

import 'src/App.scss';
import MiniApps from 'src/pages/MiniApps/MiniApps';
import ReceiptSplitter from 'src/pages/MiniApps/pages/ReceiptSplitter/ReceiptSplitter';

const Routing: React.FC = () => {
  const { user } = useContext(UserSessionContext);
  // console.log(user);
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    // if not a hash link, scroll to top
    if (hash === '') {
      window.scrollTo(0, 0);
    }
    // else scroll to id
    else {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          // NOTE: differences between scrollTo and scrollIntoView https://stackoverflow.com/questions/54681172/what-is-the-difference-between-the-different-scroll-options
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
    }
  }, [pathname, hash, key]); 

  // NOTE: these routes match the exact path/
  return (
    <>
      {/* <Routes> is an upgrade in react-router-dom v6 from v5 <Switch>. They are both exclusive in that they only choose one path/component 
    to render, but Routes apparently is better at choosing a more fitting route. for example. If '/app' and '/app/path' were declared 
    in that order (without 'exact' keyword), usually if '/app/path' is accessed, the component related to '/app' path is chosen with the <Switch> since it was declared earlier. 
    but with <Routes>, the component related to '/app/path' would be chosen even though it was declared later. That's also why I guess they also got rid of the 'exact' 
    keyword you would usually attach to <Route>, since it really isn't needed anymore  */}
      <Routes>
        <Route
          element={
            <ContentGuard
              contentType={globalConstants.CONTENT_GUARD_PAGE}
              // a 'user' that has 'basic' or 'elevated' privileges
              isAllowed={
                typeof user?.roles != 'undefined' &&
                user.roles.includes('user')
              }
            />
          }
        >
          <Route
            path='/'
            element={<Home />}
          >
            <Route
              path='/home'
              element={<Home />}
            />
          </Route>
        </Route>
        <Route
          path='/login'
          element={<Login />}
        />
        <Route
          element={
            <ContentGuard
              contentType={globalConstants.CONTENT_GUARD_PAGE}
              // a 'user' that has 'elevated' privileges
              isAllowed={
                typeof user.roles != 'undefined' &&
                user.roles.includes('user')
              }
            />
          }
        >
          <Route
            path='/design'
            element={<Design />}
          />
          <Route
            path='/design/game-dashboard-glass'
            element={<GameDashboardGlass />}
          />
          <Route
            path='/design/cat-parachute-parallax'
            element={<CatParachuteParallax />}
          />
          <Route
            path='/design/initial-beginner-parallax'
            element={<InitialBeginnerParallax />}
          />
          <Route
            path='design/simple-basic-grid'
            element={<SimpleBasicGrid />}
          />
          <Route
            path='design/simple-photo-grid'
            element={<SimplePhotoGrid />}
          />
          <Route
            path='design/simple-animated-grid'
            element={<SimpleAnimatedGrid />}
          />
          <Route
            path='design/forest-welcome-parallax'
            element={<ForestWelcomeParallax />}
          />
          <Route
            path='design/beginner-gsap-animation'
            element={<BeginnerGsapAnimation />}
          />
          <Route
            path='design/stay-afloat-animation'
            element={<StayAfloatAnimation />}
          />
          <Route
            path='mini-apps'
            element={<MiniApps />}
          />
          <Route
            path='mini-apps/receipt-splitter'
            element={<ReceiptSplitter />}
          />
          {/* Despite what https://reactrouter.com/docs/en/v6/upgrading/v5#upgrade-all-switch-elements-to-routes said, the "new and improved" 
              <Routes> does not do what it touts to do. I tried the following but no matter what I did such as 
                - removing the '/' from '/design' and '/design' from '/design/game-dashboard-glass'
                - did not render the view of <GameDashboardGlass> whenever I clicked the <Link to='/design/game-dashboard-glass'> on the <Design> page... no matter 
                how hard or how many times I tried (it did however, route to '/design/game-dashboard-glass'). I just ended up taking the '/design/game-dashboard-glass' 
                route outside of the 'design' route composition and it worked. 
          <Route
            path='/design'
            element={<Design />}
          >
            <Route
              path='/design/game-dashboard-glass'
              element={<GameDashboardGlass />}
            />
          </Route>
          */}
          {/* Didn't do design routing this way because usually the /design/<extra path indicator> indicates changes that should be done 
              to the <Design /> component. Like the overall structure of the <Design /> component would stay relatively the same. It's just
              that the <extra path indicator> would help the page change some values on the page or certain things would be displayed and other things
              wouldn't be displayed based off this value but the overall infrastructure of the page stays the same. If the page structure or contents 
              changes entirely, then you should just be creating another page, and that page should have a path to it. 
              // you could probably put something like `if path === "/design/game-dashboard-glass" then load <GameDashboardGlass>` but then what's the point? 
                you would just stray towards a different way of routing and that would make things inconsistent and confusing in your app structure
          <Route
            path='/design/*'
            element={<Design />}
          /> */}
        </Route>
        <Route
          path='/page-not-found'
          element={<PageNotFound />}
        />
        <Route
          path='/*'
          element={
            <Navigate
              to='/page-not-found'
              replace
            />
          }
        />
      </Routes>
    </>
  );
};

export default Routing;
